import { useMemo } from 'react';
import { todayISO } from '../domain/clock';
import {
  attendanceWindow, studentAttendanceHistory, studentAttendanceStats,
} from '../domain/lessonAttendance';

// A student's attendance history and what it adds up to.
//
// Derived from the expanded series rather than a stored list, so a moved or
// removed lesson is reflected without a backfill.
//
// The window, the history's order and the five figures are
// src/domain/lessonAttendance.js, so a phone's tab counts the same lessons the
// same way.

/**
 * The window this hook folds, as the pair `occurrencesBetween` takes.
 *
 * Exported for #369's studio-level balances: the window is a key in
 * `useEvents`, so a caller naming its own dates, even the same two, would
 * expand every series a second time.
 */
export { attendanceWindow };

export default function useStudentAttendance({ student, occurrencesBetween, statusFor }) {
  const history = useMemo(() => {
    if (!student) return [];
    // Offsets on the date-only grid rather than in milliseconds: the UTC day
    // of an instant is not the teacher's day, and adding 86400000ms crosses a
    // DST boundary an hour short.
    const today = todayISO();
    const [start, end] = attendanceWindow(today);
    return studentAttendanceHistory(occurrencesBetween(start, end), student.id, statusFor);
  }, [student, occurrencesBetween, statusFor]);

  const stats = useMemo(() => {
    // Only lessons that have happened count toward a rate — future bookings are
    // not absences.
    //
    // `isAttended`, not `status.startsWith('present')` (#354): "the student was
    // there" is the `attended` column of `core/src/attendance.rs`'s table, so a
    // row named `present_…` whose student did not turn up cannot be miscounted.
    //
    // `absent` stays a prefix: the table has no column for it (an absence is
    // every recorded row that is neither attended nor teacher-cancelled), and
    // the prefix keeps `unrecorded` out of `decided`. Same reasoning as the
    // attendance report in `src/domain/reportRunner.js`.
    //
    // Unrecorded lessons are excluded from the denominator: they are not known
    // to be either, and counting them as absences would report the teacher's
    // paperwork rather than the student.
    return studentAttendanceStats(history, todayISO());
  }, [history]);

  return { history, stats };
}
